import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { filenameFromContentDisposition } from '@/lib/download';
import { queryKeys } from '@/lib/query-client';
import type {
  Asset,
  AssetProblemReport,
  ReportAssetProblemResponse,
  AssetMapResponse,
  AssetTemplate,
  AssetReservation,
  Inspection,
  Deployment,
  RopeCut,
  MaintenanceRecord,
  Certification,
  PaginatedResponse,
  CreateAssetPayload,
  BulkUpdateStatusPayload,
  BulkUpdateResponse,
  CheckOutPayload,
  CheckInPayload,
  AssetStatus,
  InstalledAssetOverrunResponse,
  ReceiveGearPayload,
  ReceiveGearResponse,
  BatchDeployPayload,
  BatchDeployResponse,
  ResolveSerialsPayload,
  ResolveSerialsResponse,
} from '@/lib/types';

export interface AssetFilters {
  page?: number;
  page_size?: number;
  q?: string;
  status?: AssetStatus | string;
  manufacturer?: string;
  date_added_after?: string;
  date_added_before?: string;
  length_min?: string;
  length_max?: string;
  swl_min?: string;
  swl_max?: string;
  ordering?: string;
  include_retired?: boolean;
  include_deleted?: boolean;
  deleted_only?: boolean;
  /** Deployed/In use gear plus Quarantined/Damaged gear still checked out. */
  check_in_eligible?: boolean;
}

export function useAssets(filters: AssetFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.assets.list(cleanFilters),
    queryFn: async (): Promise<PaginatedResponse<Asset>> => {
      const response = await apiClient.get<PaginatedResponse<Asset>>('/api/v1/assets/', {
        params: cleanFilters,
      });
      return response.data;
    },
  });
}

export function useAssetMap(filters: AssetFilters = {}, enabled = true) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.assets.map(cleanFilters),
    queryFn: async (): Promise<AssetMapResponse> => {
      const response = await apiClient.get<AssetMapResponse>('/api/v1/assets/map/', {
        params: cleanFilters,
      });
      return response.data;
    },
    enabled,
  });
}

export function useAsset(id: number) {
  return useQuery({
    queryKey: queryKeys.assets.detail(id),
    queryFn: async (): Promise<Asset> => {
      const response = await apiClient.get<Asset>(`/api/v1/assets/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function findAssetInCachedLists(queryClient: QueryClient, id: number): Asset | undefined {
  const cachedLists = queryClient.getQueriesData<PaginatedResponse<Asset>>({
    queryKey: queryKeys.assets.lists(),
  });
  let newestAsset: Asset | undefined;
  let newestUpdatedAt = -1;

  for (const [queryKey, data] of cachedLists) {
    const asset = data?.results.find((result) => result.id === id);
    const updatedAt =
      queryClient.getQueryState<PaginatedResponse<Asset>>(queryKey)?.dataUpdatedAt ?? 0;
    if (asset && updatedAt >= newestUpdatedAt) {
      newestAsset = asset;
      newestUpdatedAt = updatedAt;
    }
  }
  return newestAsset;
}

export function useAssetInspections(assetId: number) {
  return useQuery({
    queryKey: queryKeys.assets.inspections(assetId),
    queryFn: async (): Promise<Inspection[]> => {
      const response = await apiClient.get<PaginatedResponse<Inspection>>(
        `/api/v1/assets/${assetId}/inspections/`
      );
      return response.data.results;
    },
    enabled: assetId > 0,
  });
}

export function useAssetProblemReports(assetId: number) {
  return useQuery({
    queryKey: queryKeys.assets.problemReports(assetId),
    queryFn: async (): Promise<AssetProblemReport[]> => {
      const reports: AssetProblemReport[] = [];
      let nextUrl: string | null = `/api/v1/assets/${assetId}/problem-reports/`;
      while (nextUrl) {
        const response: { data: PaginatedResponse<AssetProblemReport> } =
          await apiClient.get(nextUrl);
        reports.push(...response.data.results);
        nextUrl = response.data.next;
      }
      return reports;
    },
    enabled: assetId > 0,
  });
}

export interface ReportAssetProblemPayload {
  assetId: number;
  description: string;
  photo?: File | null;
}

export function useReportAssetProblem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assetId,
      description,
      photo,
    }: ReportAssetProblemPayload): Promise<ReportAssetProblemResponse> => {
      const formData = new FormData();
      formData.append('description', description);
      if (photo) {
        formData.append('photo', photo);
      }
      const response = await apiClient.post<ReportAssetProblemResponse>(
        `/api/v1/assets/${assetId}/problem-reports/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(queryKeys.assets.detail(variables.assetId), data.asset);
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useAssetHistory(assetId: number) {
  return useQuery({
    queryKey: queryKeys.assets.history(assetId),
    queryFn: async (): Promise<Deployment[]> => {
      const response = await apiClient.get<PaginatedResponse<Deployment>>(
        `/api/v1/assets/${assetId}/history/`
      );
      return response.data.results;
    },
    enabled: assetId > 0,
  });
}

export function useAssetRopeCuts(assetId: number) {
  return useQuery({
    queryKey: [...queryKeys.ropeCuts.lists(), { asset: assetId }],
    queryFn: async (): Promise<RopeCut[]> => {
      const response = await apiClient.get<PaginatedResponse<RopeCut>>('/api/v1/rope-cuts/', {
        params: { asset: assetId },
      });
      return response.data.results;
    },
    enabled: assetId > 0,
  });
}

export function useAssetMaintenance(assetId: number) {
  return useQuery({
    queryKey: [...queryKeys.maintenance.lists(), { asset: assetId }],
    queryFn: async (): Promise<MaintenanceRecord[]> => {
      const response = await apiClient.get<PaginatedResponse<MaintenanceRecord>>(
        '/api/v1/maintenance/',
        { params: { asset: assetId } }
      );
      return response.data.results;
    },
    enabled: assetId > 0,
  });
}

export function useAssetCertifications(assetId: number) {
  return useQuery({
    queryKey: [...queryKeys.certifications.lists(), { asset: assetId }],
    queryFn: async (): Promise<Certification[]> => {
      const response = await apiClient.get<PaginatedResponse<Certification>>(
        '/api/v1/certifications/',
        { params: { asset: assetId } }
      );
      return response.data.results;
    },
    enabled: assetId > 0,
  });
}

export function useAssetReservations(assetId: number) {
  return useQuery({
    queryKey: [...queryKeys.reservations.lists(), { asset: assetId }],
    queryFn: async (): Promise<AssetReservation[]> => {
      const response = await apiClient.get<PaginatedResponse<AssetReservation>>(
        '/api/v1/asset-reservations/',
        { params: { asset: assetId } }
      );
      return response.data.results;
    },
    enabled: assetId > 0,
  });
}

export function useAssetTemplates() {
  return useQuery({
    queryKey: queryKeys.templates.lists(),
    queryFn: async (): Promise<AssetTemplate[]> => {
      const response = await apiClient.get<PaginatedResponse<AssetTemplate>>(
        '/api/v1/asset-templates/'
      );
      return response.data.results;
    },
  });
}

export function useCreateAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAssetPayload): Promise<Asset> => {
      const response = await apiClient.post<Asset>('/api/v1/assets/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.map({}) });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.summary() });
    },
  });
}

export function useReceiveGear() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ReceiveGearPayload): Promise<ReceiveGearResponse> => {
      const response = await apiClient.post<ReceiveGearResponse>(
        '/api/v1/assets/receive-gear/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
    },
  });
}

export function useResolveSerials() {
  return useMutation({
    mutationFn: async (data: ResolveSerialsPayload): Promise<ResolveSerialsResponse> => {
      const response = await apiClient.post<ResolveSerialsResponse>(
        '/api/v1/assets/resolve-serials/',
        data
      );
      return response.data;
    },
  });
}

export function useBatchDeploy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BatchDeployPayload): Promise<BatchDeployResponse> => {
      const response = await apiClient.post<BatchDeployResponse>(
        '/api/v1/assets/batch-deploy/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.deployments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.staffingBoard.all });
    },
  });
}

export function useUpdateAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CreateAssetPayload> }): Promise<Asset> => {
      const response = await apiClient.patch<Asset>(`/api/v1/assets/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.detail(variables.id) });
    },
  });
}

export function useUpdateReceivedAssetManufactureDate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      batchId,
      dateOfManufacture,
    }: {
      id: number;
      batchId: string;
      dateOfManufacture: string | null;
    }): Promise<Asset> => {
      const response = await apiClient.patch<Asset>(
        `/api/v1/assets/${id}/receive-gear-manufacture-date/`,
        {
          batch_id: batchId,
          date_of_manufacture: dateOfManufacture,
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.detail(variables.id) });
    },
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<{ message: string }> => {
      const response = await apiClient.delete<{ message: string }>(`/api/v1/assets/${id}/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
    },
  });
}

export function useUndoReceivedAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      batchId,
    }: {
      id: number;
      batchId: string;
    }): Promise<{ message: string }> => {
      const response = await apiClient.post<{ message: string }>(
        `/api/v1/assets/${id}/undo-receive/`,
        { batch_id: batchId }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
    },
  });
}

export function useBulkUpdateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkUpdateStatusPayload): Promise<BulkUpdateResponse> => {
      const response = await apiClient.post<BulkUpdateResponse>(
        '/api/v1/assets/bulk_update_status/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
    },
  });
}

export function useRetireAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<Asset> => {
      const response = await apiClient.post<Asset>(`/api/v1/assets/${id}/retire/`);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.detail(id) });
    },
  });
}

export function useRestoreAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<Asset> => {
      const response = await apiClient.post<Asset>(`/api/v1/assets/${id}/restore/`);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.detail(id) });
    },
  });
}

export function useUnretireAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<Asset> => {
      const response = await apiClient.post<Asset>(`/api/v1/assets/${id}/unretire/`);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.detail(id) });
    },
  });
}

export function useCheckOutAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CheckOutPayload }): Promise<Asset> => {
      const response = await apiClient.post<Asset>(`/api/v1/assets/${id}/check_out/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.deployments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
    },
  });
}

export function useCheckInAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CheckInPayload }): Promise<Asset> => {
      const response = await apiClient.post<Asset>(`/api/v1/assets/${id}/check_in/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.deployments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.gearRequests.all });
    },
  });
}

export function useGenerateQRCode(assetId: number) {
  return useQuery({
    queryKey: [...queryKeys.assets.detail(assetId), 'qr'],
    queryFn: async (): Promise<string> => {
      const response = await apiClient.post<{ qr_code: string }>(
        `/api/v1/assets/${assetId}/generate_qr/`
      );
      return response.data.qr_code;
    },
    enabled: assetId > 0,
    staleTime: Infinity,
  });
}

export function useCheckSerialNumber(serialNumber: string) {
  return useQuery({
    queryKey: [...queryKeys.assets.all, 'check-serial', serialNumber],
    queryFn: async (): Promise<Asset | null> => {
      const response = await apiClient.get<PaginatedResponse<Asset>>('/api/v1/assets/', {
        params: { q: serialNumber, page_size: 5 },
      });
      const exact = response.data.results.find(
        (a) => a.serial_number.toLowerCase() === serialNumber.toLowerCase()
      );
      return exact || null;
    },
    enabled: serialNumber.length >= 2,
  });
}

export function useLinkNfcTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, nfc_tag_id }: { id: number; nfc_tag_id: string }): Promise<{ message: string; asset: Asset }> => {
      const response = await apiClient.post<{ message: string; asset: Asset }>(
        `/api/v1/assets/${id}/link-nfc-tag/`,
        { nfc_tag_id }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.detail(variables.id) });
    },
  });
}

export function useUnlinkNfcTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<{ message: string; asset: Asset }> => {
      const response = await apiClient.post<{ message: string; asset: Asset }>(
        `/api/v1/assets/${id}/unlink-nfc-tag/`
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.detail(id) });
    },
  });
}

export function useLookupByNfcTag() {
  return useMutation({
    mutationFn: async (tagId: string): Promise<Asset> => {
      const response = await apiClient.get<Asset>(
        '/api/v1/assets/lookup-by-tag/',
        { params: { tag_id: tagId } }
      );
      return response.data;
    },
  });
}

export function useInstalledGearOverrun(locationId: number, windowDays = 0) {
  return useQuery({
    queryKey: queryKeys.assets.inspectionOverrun(locationId, windowDays),
    queryFn: async (): Promise<InstalledAssetOverrunResponse> => {
      const response = await apiClient.get<InstalledAssetOverrunResponse>(
        '/api/v1/assets/inspection-overrun/',
        { params: { location: locationId, window_days: windowDays } },
      );
      return response.data;
    },
    enabled: locationId > 0,
  });
}

export function useExportChainOfCustody() {
  return useMutation({
    mutationFn: async (id: number): Promise<{ blob: Blob; filename: string }> => {
      const response = await apiClient.get(
        `/api/v1/assets/${id}/chain-of-custody/`,
        { responseType: 'blob' },
      );
      return {
        blob: response.data as Blob,
        filename: filenameFromContentDisposition(
          response.headers['content-disposition'],
          `ChainOfCustody-${id}.pdf`,
        ),
      };
    },
  });
}

export function useToggleInstalledInPlace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      is_installed,
      installed_at,
    }: {
      id: number;
      is_installed: boolean;
      installed_at?: string | null;
    }): Promise<Asset> => {
      const response = await apiClient.patch<Asset>(`/api/v1/assets/${id}/`, {
        is_installed,
        installed_at,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.subscription() });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.detail(variables.id) });
    },
  });
}
